import axios from 'axios'
import {mapActions} from 'vuex'
import SessionService from '@/services/SessionService'
import {LOGIN_URL, SITE_NAME, API_URL, API_TIMEOUT} from '@/config'

const ApiService = {
  ...mapActions('snackbar', [
    'showSnackbar'
  ]),
  ...mapActions('loading', [
    'setLoading',
    'unsetLoading'
  ]),

  init(store) {
    // To make actions work
    // noinspection JSUnusedGlobalSymbols
    this.$store = store

    axios.interceptors.request.use(
      config => {
        config.timeout = API_TIMEOUT
        config.headers['Pragma'] = 'no-cache'
        config.headers['Cache-Control'] = 'no-cache, no-store'
        if (SessionService.isLoggedIn()) {
          config.headers['Authorization'] = `Bearer ${SessionService.getToken()}`
        }
        return config
      }
    )

    axios.interceptors.response.use(
      response => response,
      async error => {
        if (!!error && !!error.response && error.response.status === 401) {
          this.checkVersion(error.response.headers, true)
          location.href = `${LOGIN_URL}?nombre=${SITE_NAME}&redirect=${location.href}`
        }
        return Promise.reject(error)
      }
    )
  },

  handleError(error) {
    let temporaryError = false
    // Network Errors
    if (error.message === 'Network Error') {
      temporaryError = true
      this.showSnackbar(['Compruebe su conexión a internet y vuelva a intentar'])
      /* eslint-disable no-console */
      console.log(`Error de red: ${error}`)
      // Unauthorized
    } else if (error.response && error.response.status === 401) {
      // If is not authorized, avoid unnecessary snackbar
      /* eslint-disable no-console */
      console.log(`Sesión caducada o token no autorizado: ${error}`)
      // Server (Backend) Errors
    } else if (error.response && (error.response.data || error.response.data === '')) {
      // Marked as Error by Backend Developer
      if (error.response.data.estado === false) {
        this.showSnackbar([error.response.data.mensaje])
        /* eslint-disable no-console */
        console.log(`BackendError: ${error.response.data.mensaje}`)
      } else {
        // Other Standard Server Errors
        if (error.response.status === 404) {
          this.showSnackbar(['Error inesperado. Vuelva a intentar más tarde.'])
          /* eslint-disable no-console */
          console.log(`Not found: ${error}`)
        } else if (error.response.status <= 499) {
          this.showSnackbar(['Error inesperado. Vuelva a intentar más tarde.'])
          /* eslint-disable no-console */
          console.log(`Client error: ${error}`)
        } else if (error.response.status === 503) {
          this.showSnackbar(['Servicio en mantenimiento. Vuelva a intentar más tarde.'])
        } else if (error.response.status >= 500) {
          this.showSnackbar(['Error inesperado. Vuelva a intentar más tarde.'])
          /* eslint-disable no-console */
          console.log(`Internal server Error: ${error}`)
          // Could be a Gateway Timeout e.g. from Nginx (only if status > 500)
          temporaryError = error.response.status > 500
        }
      }
      // Unknown Errors
    } else {
      this.showSnackbar(['Error inesperado. Vuelva a intentar más tarde.'])
      /* eslint-disable no-console */
      console.log(`Error desconocido: ${error}`)
    }

    if (!!error && !!error.response && !!error.response.headers) {
      this.checkVersion(error.response.headers)
    }

    error.name = 'FhNetworkError'
    if (temporaryError) {
      error.name = 'FhTemporaryError'
    }
    throw error
  },

  handleResponse(response) {
    this.checkVersion(response.headers)

    if (response.data.estado !== true) {
      let e = new Error(response.data.mensaje)
      e.name = 'FhBackendError'
      e.response = response
      throw e
    }
    return response.data.resultado
  },

  checkVersion (headers, forceReload=false) {
    let serverVersion = headers['x-spa-version']
    let spaVersion = process.env.VUE_APP_SPA_VERSION

    if (serverVersion && spaVersion && serverVersion !== spaVersion) {
      // Permitir que otras notificaciones puedan ser mostradas antes que esta
      setTimeout(() => {
        this.showSnackbar(['Nueva version disponible', 'success', 'Actualizar', () => window.location.reload()])
      }, 200)

      if (forceReload) {
        // Recargar la página una sola vez para la misma versión
        let lastServerVersion = localStorage.getItem('version')
        if (!!lastServerVersion && lastServerVersion === serverVersion) {
          return
        }
        localStorage.setItem('version', serverVersion)

        window.location.reload()

        // Prevenir procesamiento subsiguiente
        throw Error('Nueva version disponible. Por favor recargue la página.')
      }
    }
  },

  async get(resource, params = {}) {
    let loadId = 'get:' + resource
    this.setLoading(loadId)

    try {
      let resp = await axios.get(`${API_URL}/${resource}`, {params})

      return this.handleResponse(resp)
    } catch (error) {
      this.handleError(error)
    } finally {
      this.unsetLoading(loadId)
    }
  },

  async post(resource, data) {
    let loadId = 'post:' + resource
    this.setLoading(loadId)

    try {
      let resp = await axios.post(`${API_URL}/${resource}`, data)

      return this.handleResponse(resp)
    } catch (error) {
      this.handleError(error)
    } finally {
      this.unsetLoading(loadId)
    }
  },

  async download (resource, params = {}) {
    let loadId = 'download:' + resource
    this.setLoading(loadId)

    axios.interceptors.response.use(
      response => {
        return response
      },
      error => {
        if (
          error.request.responseType === 'blob' &&
          error.response.data instanceof Blob &&
          error.response.data.type &&
          error.response.data.type.toLowerCase().indexOf('json') != -1
        ) {
          return new Promise((resolve, reject) => {
            let reader = new FileReader()
            reader.onload = () => {
              error.response.data = JSON.parse(reader.result)
              resolve(Promise.reject(error))
            }
            reader.onerror = () => {
              reject(error)
            }
            reader.readAsText(error.response.data)
          })
        }
        return Promise.reject(error)
      }
    )

    try {
      return await axios.get(`${API_URL}/${resource}`,
        { params, responseType: 'blob'})
    } catch (error) {
      this.handleError(error)
    } finally {
      this.unsetLoading(loadId)
    }
  }
}

export default ApiService
